import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomerPaymentApi from './customerPaymentApi';
import { CustomerPayment } from './customerPaymentType';
import { formatAddress, formatTimestamp,formatDate } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { FilePlus } from 'lucide-react';
import { InvoiceProjectCustomer } from '../invoice/invoiceType';
import invoiceApi from '../invoice/invoiceApi';

// Payment methods
const PAYMENT_METHODS = ['bank_transfer', 'credit_card', 'cash', 'check', 'money_order'];

const PAYMENT_METHOD_LABELS: { [key: string]: string } = {
  bank_transfer: 'Bank Transfer',
  credit_card: 'Credit Card',
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
};

const CreateCustomerPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoice: stateData } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Current invoice data being worked with
  const [invoiceData, setInvoiceData] = useState<InvoiceProjectCustomer | null>(null);
  
  // Scenario 2: All invoices for selection
  const [allInvoices, setAllInvoices] = useState<InvoiceProjectCustomer[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');

  // Existing payments for the selected invoice
  const [existingPayments, setExistingPayments] = useState<CustomerPayment[]>([]);
  
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    note: '',
  });

  const breadcrumbs = [
    { label: 'Customer Payment', path: '/main/customerpayment' },
    { label: 'Add New Payment', path: '' },
  ];

  // Initialize - check if we have state data or need to fetch invoices
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      setErrorMessage(null);

      try {
        // Scenario 1: State data provided (flat InvoiceProjectCustomer)
        if (stateData && stateData.invoice_id) {
          setInvoiceData(stateData);
          // Fetch existing payments for this invoice
          try {
            const payments = await CustomerPaymentApi.getCustomerPaymentByInvoiceId(String(stateData.invoice_id));
            setExistingPayments(payments);
          } catch (error) {
            // If no payments exist yet, that's okay
            setExistingPayments([]);
          }
        } 
        // Scenario 2: No state data, fetch all invoices
        else {
          const invoices = await invoiceApi.getInvoicesProjectsCustomers();
          setAllInvoices(invoices);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [stateData]);

  // When user selects an invoice (scenario 2), fetch its payments
  useEffect(() => {
    const fetchPayments = async () => {
      if (!selectedInvoiceId) {
        setExistingPayments([]);
        return;
      }

      setLoading(true);
      setErrorMessage(null);
      
      try {
        const payments = await CustomerPaymentApi.getCustomerPaymentByInvoiceId(selectedInvoiceId);
        setExistingPayments(payments);
      } catch (error) {
        // No payments yet is okay
        setExistingPayments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [selectedInvoiceId]);

  const handleInvoiceSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const invoiceId = e.target.value;
    setSelectedInvoiceId(invoiceId);
    setMessage(null);
    setErrorMessage(null);
    
    // Find and set the selected invoice data
    const selected = allInvoices.find(inv => String(inv.invoice_id) === invoiceId);
    if (selected) {
      setInvoiceData(selected);
    } else {
      setInvoiceData(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateBalance = () => {
    if (!invoiceData) return 0;
    
    const finalTotal = Number(invoiceData.final_total) || 0;
    const totalPaid = existingPayments.reduce((sum, payment) => sum + Number(payment.amount), 0);
    return finalTotal - totalPaid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setMessage(null);

    if (!invoiceData) {
      setErrorMessage('Please select an invoice first.');
      return;
    }

    const paymentAmount = parseFloat(formData.amount);

    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setErrorMessage('Please enter a valid payment amount greater than 0.');
      return;
    }

    const balance = calculateBalance();
    if (paymentAmount > balance) {
      setErrorMessage(`Payment amount cannot exceed balance due ($${balance.toFixed(2)}).`);
      return;
    }

    try {
      const customerPaymentPayload: Omit<CustomerPayment, 'customer_payment_id' | 'created_at' | 'updated_at'> = {
        invoice_id: String(invoiceData.invoice_id),
        customer_id: String(invoiceData.customer_id),
        project_id: String(invoiceData.project_id),
        amount: paymentAmount,
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        note: formData.note || undefined,
      };

      const newPayment = await CustomerPaymentApi.createCustomerPayment(customerPaymentPayload);
      
      // Update existing payments list
      setExistingPayments(prev => [...prev, newPayment]);

      setMessage('Successfully added a new payment.');
      
      // Reset form
      setFormData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        note: '',
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const formatCustomerAddress = () => {
    if (!invoiceData) return '';
    return formatAddress(
      invoiceData.customer_address_1,
      invoiceData.customer_address_2,
      invoiceData.customer_city,
      invoiceData.customer_state,
      invoiceData.customer_zip
    );
  };

  // Calculate dynamic total paid including the current form amount
  const existingPaymentsTotal = existingPayments.reduce((sum, payment) => sum + Number(payment.amount), 0);
  const currentFormAmount = Number(formData.amount) || 0;
  const totalPaid = existingPaymentsTotal + currentFormAmount;
  
  const balance = calculateBalance();
  // Dynamic balance that updates as user types
  const dynamicBalance = balance - currentFormAmount;
  const isBalanceZero = balance <= 0;

  if (loading) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Add New Payment</h2>

          {/* Error Message */}
          {errorMessage && !loading && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {/* Scenario 2: Invoice Selection */}
          {!stateData && allInvoices.length > 0 && (
            <div className="mb-6">
              <Label htmlFor="invoice-select" className="mb-2">
                Select Invoice <span className="text-red-500">*</span>
              </Label>
              <Select
                id="invoice-select"
                value={selectedInvoiceId}
                onChange={handleInvoiceSelect}
              >
                <option value="">Select an Invoice</option>
                {allInvoices.map((inv) => {
                  const invBalance = (Number(inv.final_total) || 0) - (Number(inv.amount_paid) || 0);
                  return (
                    <option key={inv.invoice_id} value={inv.invoice_id}>
                      Invoice #{inv.invoice_id}: {inv.name} - {inv.customer_first_name} {inv.customer_last_name} - 
                      Balance: ${invBalance.toFixed(2)}
                    </option>
                  );
                })}
              </Select>
            </div>
          )}

          {/* Display invoice details when selected */}
          {invoiceData && (
            <>
              {/* Customer Information */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">Customer:</span>{' '}
                  {invoiceData.customer_first_name} {invoiceData.customer_last_name}
                  {', '}
                  <span className="font-semibold">Address:</span> {formatCustomerAddress()}
                </p>
              </div>

              {/* Project Information */}
              <div className="p-4 bg-white border border-cyan-200 rounded-lg mb-6">
                <p className="text-sm text-foreground">
                  <span className="font-semibold">Project:</span>{' '}
                  {invoiceData.project_name || `Project #${invoiceData.project_id}`}
                  {invoiceData.project_description && (
                    <>
                      {', '}
                      <span className="font-semibold">Description:</span> {invoiceData.project_description}
                    </>
                  )}
                  {invoiceData.project_type && (
                    <>
                      {', '}
                      <span className="font-semibold">Type:</span> {invoiceData.project_type}
                    </>
                  )}
                </p>
              </div>

              {/* Invoice Information */}
              <div className="p-4 bg-white border border-gray-300 rounded-lg mb-6">
                <div className="text-sm space-y-2">
                  <div>
                    <span className="font-semibold">Invoice:</span>{' '}
                    {invoiceData.name || 'N/A'}
                    {' • '}
                    <span className="font-semibold">ID:</span> {invoiceData.invoice_id}
                  </div>
                  <div>
                    <span className="font-semibold">Issue Date:</span> {formatDate(invoiceData.issue_date)}
                    {' • '}
                    <span className="font-semibold">Due Date:</span> {formatDate(invoiceData.due_date)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
                    <div>
                      <span className="text-gray-600">Subtotal:</span>{' '}
                      <span className="font-semibold">${Number(invoiceData.sub_total_sum_lineitems || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Tax ({Number(invoiceData.tax_rate || 0).toFixed(2)}%):</span>{' '}
                      <span className="font-semibold">${Number(invoiceData.tax_amount || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Discount:</span>{' '}
                      <span className="font-semibold text-green-600">-${Number(invoiceData.discount_usd || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Final Total:</span>{' '}
                      <span className="font-semibold">${Number(invoiceData.final_total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div>
                      <span className="text-gray-600">Total Paid:</span>{' '}
                      <span className="font-semibold text-green-600">${totalPaid.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Balance Due:</span>{' '}
                      <span className={`font-semibold ${dynamicBalance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${dynamicBalance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing Payments */}
              {existingPayments.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h3 className="text-base font-semibold text-foreground mb-3">Existing Payments</h3>
                  <div className="space-y-2">
                    {existingPayments.map((payment, index) => (
                      <div key={payment.customer_payment_id} className="flex justify-between items-center text-sm">
                        <span>
                          {index + 1}. {formatTimestamp(payment.payment_date)} - {PAYMENT_METHOD_LABELS[payment.payment_method]}
                        </span>
                        <span className="font-semibold text-green-600">${Number(payment.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Form */}
              {!isBalanceZero ? (
                <div className="p-6 bg-gray-50 border-2 border-gray-500 rounded-lg">
                  <h3 className="text-base font-semibold text-foreground mb-4">
                    New Payment Details
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
                    <div>
                      <Label htmlFor="amount">
                        Amount ($) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0.01"
                        max={balance}
                        required
                        placeholder={`Max: ${balance.toFixed(2)}`}
                      />
                    </div>

                    <div>
                      <Label htmlFor="payment_date">
                        Payment Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        id="payment_date"
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="payment_method">
                        Payment Method <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        id="payment_method"
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Method</option>
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method} value={method}>
                            {PAYMENT_METHOD_LABELS[method] || method}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="note">Note (optional)</Label>
                      <textarea
                        id="note"
                        name="note"
                        value={formData.note}
                        onChange={handleInputChange}
                        maxLength={2500}
                        placeholder="Add any additional notes..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical min-h-[100px]"
                      />
                    </div>

                    <Button type="submit" size="medium" variant="default">
                      <FilePlus size={20} />
                      Save Payment
                    </Button>
                  </form>
                </div>
              ) : (
                <Alert variant="success">
                  This invoice has been paid in full. No additional payment is needed.
                </Alert>
              )}
            </>
          )}

          {/* No invoice selected message */}
          {!stateData && !invoiceData && allInvoices.length > 0 && !loading && (
            <Alert variant="info">
              Please select an invoice to add a payment.
            </Alert>
          )}

          {/* No invoices available */}
          {!stateData && allInvoices.length === 0 && !loading && (
            <Alert variant="info">
              No invoices available. Please create an invoice first.
            </Alert>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateCustomerPayment;